import {
  ArrayField,
  Datagrid,
  FunctionField,
  Loading,
  NumberField as RaNumberField,
  ReferenceField,
  SimpleShowLayout,
  TextField as RaTextField,
  Title as RaTitle,
  useGetList,
  useGetOne,
  useListContext,
  useTranslate,
  useRecordContext
} from 'react-admin'
import {
  Box,
  Card,
  Grid,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material'
import { format, parseISO, startOfMonth } from 'date-fns/fp'
import {
  find,
  isBoolean,
  isNil,
  join,
  map,
  slice,
  split,
  toNumber
} from 'lodash/fp'
import log from 'loglevel'
import { assign } from 'lodash'

import { InvoiceItem, PartnerInvoice } from '../../types'
import { Title } from '../Util'
import { endOfMonth } from 'date-fns'

log.setLevel(log.levels.DEBUG)

const NumberField: React.FC<{
  source: string
  label?: string | boolean
}> = ({ source }) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const record = useRecordContext()
  return (
    <Grid container>
      <Grid item xs={isDesktop ? 1 : 5} md={1.5} lg={1}>
        <Typography variant="inherit">
          {translate(`resources.partnerInvoices.display.${source}`)}
        </Typography>
      </Grid>
      <Grid item xs={isDesktop ? 1 : 3} sx={{ textAlign: 'right' }}>
        <Typography variant="inherit">
          {record[source].toLocaleString()}
          {translate(`resources.partnerInvoices.unit.${source}`)}
        </Typography>
      </Grid>
    </Grid>
  )
}
const TextField: React.FC<{
  source: string
  label?: string | boolean
  variant?: any
}> = ({ source, label, variant }) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const record = useRecordContext()
  const labelText = translate(
    `resources.partnerInvoices.display.${
      !isBoolean(label) && !isNil(label) ? label : source
    }`
  )
  return isDesktop ? (
    <Typography
      variant={!isNil(variant) ? variant : isDesktop ? 'inherit' : 'subtitle2'}>
      {labelText}
      {record[source]}
    </Typography>
  ) : (
    <Grid container>
      {labelText !== '' && (
        <Grid item xs={6}>
          <Typography
            variant={
              !isNil(variant) ? variant : isDesktop ? 'inherit' : 'subtitle2'
            }>
            {labelText}
          </Typography>
        </Grid>
      )}
      <Grid item xs={isDesktop ? 10 : 12}>
        <Typography variant={!isNil(variant) ? variant : 'inherit'}>
          {record[source]}
        </Typography>
      </Grid>
    </Grid>
  )
}
const DateField: React.FC<{
  source: string
  label?: string | boolean
}> = ({ source, label }) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const record = useRecordContext()
  return isDesktop ? (
    <>
      <Typography variant="inherit">
        {translate(
          `resources.partnerInvoices.display.${
            !isBoolean(label) && !isNil(label) ? label : source
          }`
        )}
        {format('yyyy年M月d日')(parseISO(`${record[source] as string}T00:00`))}
      </Typography>
    </>
  ) : (
    <Grid container>
      <Grid item xs={isDesktop ? 0.8 : 10} md={1}>
        <Typography variant="subtitle2">
          {translate(
            `resources.partnerInvoices.display.${
              !isBoolean(label) && !isNil(label) ? label : source
            }`
          )}
        </Typography>
      </Grid>
      <Grid item xs={isDesktop ? 11 : 10}>
        <Typography variant="inherit">
          {format('yyyy年M月d日')(
            parseISO(`${record[source] as string}T00:00`)
          )}
        </Typography>
      </Grid>
    </Grid>
  )
}
const ItemsField: React.FC = () => {
  const { data } = useListContext()
  return (
    <>
      {map((item: any) => (
        <SimpleShowLayout record={item}>
          <TextField source="name" label={false} />
          <NumberField source="count" label={false} />
          <TextField source="duration" label={false} />
          <NumberField source="price" label={false} />
          <NumberField source="amount" label={false} />
        </SimpleShowLayout>
      ))(data)}
    </>
  )
}
const Items: React.FC<{
  invoice: PartnerInvoice
  start: number
}> = ({ invoice, start }) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const products = split('-')(invoice.product)
  const partner = join('-')(slice(0, 5)(products))
  const { data, isLoading } = useGetOne('partners', {
    id: partner
  })
  if (isLoading) return <Loading />
  const product = data.products[toNumber(products[5])]
  const { name } = product
  const items = map((item: InvoiceItem) => {
    const { count, price, amount } = item
    return {
      name,
      count,
      duration: `${format('yyyy年M月d日')(start)}〜${format('yyyy年M月d日')(
        endOfMonth(start)
      )}`,
      price,
      amount
    }
  })(invoice.items)
  const { sum } = invoice
  const tax = sum * 0.1
  const sumWithTax = tax + sum
  const itemData = {
    id: 0,
    items,
    sum,
    tax,
    sumWithTax
  }
  return (
    <>
      <Typography variant="h6">
        {translate('resources.partnerInvoices.display.items')}
      </Typography>
      <SimpleShowLayout record={itemData}>
        <NumberField source="sum" label={false} />
        <NumberField source="tax" label={false} />
        <NumberField source="sumWithTax" label={false} />
      </SimpleShowLayout>
      <SimpleShowLayout record={itemData}>
        {isDesktop ? (
          <ArrayField source="items" label="">
            <Grid container>
              <Grid item md={8} lg={6} xl={5}>
                <Datagrid bulkActionButtons={false}>
                  <RaTextField
                    source="name"
                    label="resources.partnerInvoices.display.name"
                  />
                  <RaNumberField
                    source="count"
                    label="resources.partnerInvoices.display.count"
                  />
                  <RaTextField
                    source="duration"
                    label="resources.partnerInvoices.display.duration"
                  />
                  <RaNumberField
                    source="price"
                    label="resources.partnerInvoices.display.price"
                    locales="ja-JP"
                    options={{
                      style: 'currency',
                      currency: 'JPY',
                      currencyDisplay: 'name'
                    }}
                  />
                  <RaNumberField
                    source="amount"
                    label="resources.partnerInvoices.display.amount"
                    locales="ja-JP"
                    options={{
                      style: 'currency',
                      currency: 'JPY',
                      currencyDisplay: 'name'
                    }}
                  />
                </Datagrid>
              </Grid>
            </Grid>
          </ArrayField>
        ) : (
          <ArrayField source="items" label={false}>
            <ItemsField />
          </ArrayField>
        )}
      </SimpleShowLayout>
    </>
  )
}

export const InvoiceShow: React.FC<{ id: string }> = ({ id }) => {
  const translate = useTranslate()
  const now = Date.now()
  const { data: invoices, isLoading } = useGetList('invoices', {
    filter: {
      date: { eq: format('yyyy-MM-dd')(startOfMonth(now)) }
    },
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'partner', order: 'DESC' }
  })
  if (isLoading) return <Loading />
  const invoice = find((item: PartnerInvoice) => item.partner === id)(invoices)
  if (isNil(invoice)) return <></>
  const company = translate('resources.partnerInvoices.company')
  const companyAddress = translate('resources.partnerInvoices.companyAddress')
  const companyNumber = translate('resources.partnerInvoices.companyNumber')
  assign(invoice, { company })
  assign(invoice, { companyAddress })
  assign(invoice, { companyNumber })
  return (
    <Card>
      <RaTitle title={<Title title="mine.users.invoice" />} />
      <SimpleShowLayout record={invoice}>
        <DateField source="date" label={false} />
        <ReferenceField
          source="partner"
          reference="partners"
          label={false}
          link={false}>
          <TextField source="name" label="partner" />
        </ReferenceField>
        <Box sx={{ marginTop: '0.5em', marginBottom: '0.5em' }}>
          <TextField source="company" label={false} />
          <TextField source="companyNumber" label={false} variant="subtitle2" />
          <FunctionField
            source="companyAddress"
            label={translate(
              'resources.partnerInvoices.display.companyAddress'
            )}
            render={(record: { companyAddress: string }) =>
              map((address: string) => (
                <Typography variant="inherit">{address}</Typography>
              ))(split('\n')(record.companyAddress))
            }
          />
        </Box>
        <Items invoice={invoice} start={now} />
      </SimpleShowLayout>
    </Card>
  )
}
